import { FC, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { FaFile, FaFileSignature, FaListAlt } from 'react-icons/fa';
import { useQuery } from 'react-query';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { useModal } from 'store/modal';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientEntity } from 'utils/helpers/client';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';

import { ISelectOptionProps } from 'components/form/types';
import { IАttachment } from 'components/Attachment';
import Button from 'components/Button';
import FetchTable from 'components/table/FetchTable';
import TooltipWrapper from 'components/TooltipWrapper';
import { buildFilters } from 'components/table/utils/filtersConfig';
import Spinner from 'components/Spinner';

import InnerWrapper from '../InnerWrapper';

interface IDropdowns {
  status?: ISelectOptionProps[];
}

interface IButton {
  type: string;
  text?: string;
  action_link?: string;
}

const COMPLETED_STATUS = 'report_signed_by_employer';

const CompletedScreen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal } = useModal();
  const [loadingPreviewKey, setLoadingPreviewKey] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const {
    isFetching,
    isLoading,
    data: dropdowns,
  } = useQuery<IDropdowns, Error>(
    [buildClientEntity('my-work-trips-completed-dropdowns', client)],
    async () => {
      const res = await API.get('work-trips/dropdown-options');
      return res.data?.[language || 'bg'];
    },
    { cacheTime: 0 }
  );

  const tableFilters = useMemo(() => {
    if (!dropdowns) return null;

    const statusWithDefaults = dropdowns.status?.map(opt => {
      if (String(opt.value) === COMPLETED_STATUS) {
        return { ...opt, selected: true };
      }
      return opt;
    });

    return [
      buildFilters({
        name: 'from_date_gte',
        placeholder: t('leaves.dateFrom'),
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'to_date_lte',
        placeholder: t('leaves.dateTo'),
        type: 'monthyearpickerend',
      }),
      buildFilters({
        name: 'status',
        placeholder: t('workTrips.status'),
        options: statusWithDefaults,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ];
  }, [dropdowns, t]);

  const getDocIdFromActionLink = (actionLink?: string): number | null => {
    if (!actionLink) return null;

    const match = actionLink.match(/\/documents\/(\d+)/);
    if (!match?.[1]) return null;

    return Number(match[1]);
  };

  const getReportAttachments = (rowData: any): IАttachment[] => {
    const normalizeAttachments = (attachments: any[]): IАttachment[] =>
      attachments
        .map(item => ({
          id: item?.id,
          file_name: item?.file_name || item?.name || `#${item?.id}`,
          extension: item?.extension || item?.file_extension || '',
        }))
        .filter(item => item.id);

    if (Array.isArray(rowData.attachments)) {
      return normalizeAttachments(rowData.attachments);
    }

    if (Array.isArray(rowData.report_documents)) {
      return normalizeAttachments(rowData.report_documents);
    }

    if (Array.isArray(rowData.report_files)) {
      return normalizeAttachments(rowData.report_files);
    }

    if (Array.isArray(rowData.report_attachment_document_ids)) {
      return rowData.report_attachment_document_ids
        .filter(Boolean)
        .map((id: number) => ({ id, file_name: `#${id}`, extension: '' }));
    }

    return [];
  };

  const handleAttachmentClick = (id: number) => {
    return API.get(`work-trips/attachment/${id}/download`)
      .then(res => {
        window.open(res.data, '_blank');
      })
      .catch(err => {
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        });
      });
  };

  const handleAttachmentsModal = (attachments: IАttachment[], heading?: string) => {
    const links = attachments.map(attachment => ({
      id: attachment.id,
      fileName: attachment.extension ? `${attachment.file_name}.${attachment.extension}` : attachment.file_name,
    }));

    showModal({
      type: 'list',
      heading: heading || t('workTrips.previewReportDocuments'),
      onLinkClick: handleAttachmentClick,
      links,
    });
  };

  const handlePreviewClick = (docId: number, key: string) => {
    setLoadingPreviewKey(key);
    handlePreviewDoc({
      showModal,
      id: docId,
      setIsLoading: setIsLoadingPreview,
    });
  };

  const renderActions = (buttons: IButton[] = [], rowData: any) => {
    const previewOrderButton = buttons.find(button => button.type === 'order_preview');
    const previewReportButton = buttons.find(button => button.type === 'report_preview');
    const attachmentsButton = buttons.find(button => button.type === 'attachments');
    const previewOrderDocId = rowData.order_document_id || getDocIdFromActionLink(previewOrderButton?.action_link);
    const previewReportDocId = rowData.report_document_id || getDocIdFromActionLink(previewReportButton?.action_link);
    const reportAttachments = getReportAttachments(rowData);
    const orderPreviewKey = `${rowData.id}-order-preview`;
    const reportPreviewKey = `${rowData.id}-report-preview`;

    return (
      <>
        {previewOrderButton && previewOrderDocId && (
          <span className="d-inline-block ml-2 mb-1">
            <TooltipWrapper
              id={`tooltip-order-completed-${rowData.id}`}
              overlay={previewOrderButton.text || t('workTrips.previewOrder')}
            >
              <Button
                type="button"
                btnType="primary"
                size="sm"
                disabled={loadingPreviewKey === orderPreviewKey && isLoadingPreview}
                onClick={() => handlePreviewClick(previewOrderDocId, orderPreviewKey)}
              >
                {!(loadingPreviewKey === orderPreviewKey && isLoadingPreview) && <FaFileSignature size={14} />}
                {loadingPreviewKey === orderPreviewKey && isLoadingPreview && <Spinner size={14} />}
              </Button>
            </TooltipWrapper>
          </span>
        )}

        {previewReportButton && previewReportDocId && (
          <span className="d-inline-block ml-2 mb-1">
            <TooltipWrapper
              id={`tooltip-report-completed-${rowData.id}`}
              overlay={previewReportButton.text || t('workTrips.previewReport')}
            >
              <Button
                type="button"
                btnType="secondary"
                size="sm"
                disabled={loadingPreviewKey === reportPreviewKey && isLoadingPreview}
                onClick={() => handlePreviewClick(previewReportDocId, reportPreviewKey)}
              >
                {!(loadingPreviewKey === reportPreviewKey && isLoadingPreview) && <FaFile size={14} />}
                {loadingPreviewKey === reportPreviewKey && isLoadingPreview && <Spinner size={14} />}
              </Button>
            </TooltipWrapper>
          </span>
        )}

        {attachmentsButton && reportAttachments.length > 0 && (
          <span className="d-inline-block ml-2 mb-1">
            <TooltipWrapper
              id={`tooltip-report-docs-completed-${rowData.id}`}
              overlay={attachmentsButton.text || t('workTrips.previewReportDocuments')}
            >
              <Button
                type="button"
                btnType="secondary"
                size="sm"
                onClick={() => handleAttachmentsModal(reportAttachments, attachmentsButton.text)}
              >
                <FaListAlt size={14} />
              </Button>
            </TooltipWrapper>
          </span>
        )}
      </>
    );
  };

  const columns: Array<Column<any>> = [
    {
      Header: t('workTrips.period'),
      accessor: 'from_date',
      width: '12%',
      Cell: ({ row }) => (
        <>
          {row.original.from_date ? formatDate(new Date(row.original.from_date), 'dd.mm.yyy') : ''} -{' '}
          {row.original.to_date ? formatDate(new Date(row.original.to_date), 'dd.mm.yyy') : ''}
        </>
      ),
    },
    {
      Header: t('workTrips.task'),
      accessor: 'task',
      Cell: ({ row }) => {
        const { task } = row.original;
        const taskPreview = task.length > 40 ? `${task.substr(0, 40)}...` : task;
        return taskPreview;
      },
    },
    {
      Header: t('workTrips.destination'),
      accessor: 'destination',
      Cell: ({ row }) =>
        row.original.route ? (
          <TooltipWrapper
            id={`tooltip-work-trips-route-${row.original.id}`}
            overlay={`${t('workTrips.route')}: ${row.original.route}`}
          >
            <>{row.original.displayed_destination}</>
          </TooltipWrapper>
        ) : (
          <>{row.original.displayed_destination}</>
        ),
    },
    {
      Header: t('workTrips.status'),
      accessor: 'status',
      Cell: ({ row }) => <>{t(`workTrips.statuses.${row.original.status}`)}</>,
    },
    {
      Header: () => <div className="text-right">{t('workTrips.actions')}</div>,
      accessor: 'actions',
      width: '14%',
      Cell: ({ row }) => <div style={{ textAlign: 'right' }}>{renderActions(row.original.buttons, row.original)}</div>,
    },
  ];

  return (
    <InnerWrapper>
      {tableFilters && (
        <FetchTable
          loading={isFetching || isLoading}
          entityName={buildClientEntity('my-work-trips-completed', client)}
          entityEndpoint="/work-trips"
          columns={columns}
          filters={tableFilters}
          pageLimit={20}
          pagination
          hideSearch
        />
      )}
    </InnerWrapper>
  );
};

export default CompletedScreen;
